<template>
  <eg-b-row class="px-4 py-3 essay-header-wrapper">
    <eg-b-col class="essay-header-container d-flex justify-content-between p-0">
      <div class="d-flex align-items-center">
        <eg-button
          type="ordinary"
          size="medium"
          icon="arrow-left"
          icon-size="medium"
          icon-color="var(--color-icon-neutral-default)"
          class="back-button mr-1"
          :disabled="paginationIsLoading"
          @click="goToFolder"
        />
        <h1
          v-b-tooltip.hover
          :title="essay.folder.name"
          class="essay-title text-truncate heading-m mb-0"
        >
          {{ essay.folder.name }}
        </h1>
      </div>
      <div
        class="header-actions text-right d-flex align-items-center justify-content-end"
      >
        <essay-actions-dropdown
          :essay="essay"
          variant="link"
          custom-button-class="custom-button-style"
          @delete="deleteSingleEssay"
          @regrade="regradeHandler"
          @print="printEssayFeedback"
          @export="({ type }) => $bvModal.show(`export-to-${type}-modal`)"
          @rename="$bvModal.show(`essay-rename-modal`)"
          @copy="copyData($event)"
        >
          <template #button-content>
            <eg-button type="secondary" right-icon="chevron-down">
              {{ $t("common.actions") }}
            </eg-button>
          </template>
        </essay-actions-dropdown>
        <div class="divider-vertical"></div>
        <eg-button
          type="secondary"
          size="medium"
          icon="refresh-rotate"
          @click="regradeHandler"
        >
          {{ $t("essays.regrade") }}
        </eg-button>
        <essay-review-button
          :essay="essay"
          :status="essay.status"
          @statusChangeSuccess="handleStatusChangeSuccess"
        />
        <div v-if="pageInfo" class="pagination d-flex align-items-center">
          <eg-button
            type="ordinary"
            size="medium"
            icon="chevron-left"
            icon-size="medium"
            icon-color="var(--color-icon-neutral-default, #1C0F30)"
            :disabled="paginationIsLoading || !pageInfo.hasPreviousPage"
            @click="goPrev"
          />
          <div class="mx-2">
            {{ pageInfo.current }}
            {{ $t("common.of") }}
            {{ pageInfo.totalCount }}
          </div>
          <eg-button
            type="ordinary"
            size="medium"
            icon="chevron-right"
            icon-size="medium"
            icon-color="var(--color-icon-neutral-default, #1C0F30)"
            :disabled="paginationIsLoading || !pageInfo.hasNextPage"
            @click="goNext"
          />
        </div>
      </div>
    </eg-b-col>
  </eg-b-row>
</template>

<script>
import Vue from "vue";
import hotkeys from "hotkeys-js";
import regradeMixin from "@/mixins/regradeMixin";
import { deleteEssay } from "@/api";
import { getIntensityDisplayText, formatDefaultType } from "@/utils/format";
import formatDate from "@/utils/formatDate";
import EssayActionsDropdown from "@/components/essay/essay_actions_dropdown.vue";
import EssayReviewButton from "@/components/essay/status/essay_review_button.vue";
import feedbackReport from "@/components/essay/essay/v3/feedback_report_v3.vue";
import grammarReport from "@/components/essay/essay/v3/grammar_report_v3.vue";
import EssayRegradeModal from "@/components/essay/essay_regrade_modal.vue";
import strIsHTML from "@/utils/strIsHTML";
import copyRichText from "@/utils/copyRichText";

export default {
  components: {
    EssayActionsDropdown,
    EssayReviewButton,
  },

  mixins: [
    // this.regrade
    regradeMixin,
  ],

  props: {
    essay: {
      type: Object,
      required: true,
    },
    getEssay: {
      type: Function,
      required: true,
    },
    pageInfo: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      paginationIsLoading: false,
    };
  },

  computed: {
    essayClass() {
      return this.essay.class;
    },
    gradedDate() {
      return formatDate(this.essay.createdAt);
    },
  },

  async created() {
    hotkeys("right", this.goNext);
    hotkeys("left", this.goPrev);
  },

  beforeDestroy() {
    hotkeys.unbind("right", this.goNext);
    hotkeys.unbind("left", this.goPrev);
  },
  methods: {
    formatDefaultType,
    getIntensityDisplayText,

    async copyData(type) {
      // NOTE: copyRichText should run firstly and immediately, any async operation should be inside copyRichText
      // first we create a pomise that wait when vue component fully loaded, and save it to reuse for richText and plainText
      let vueInstancePromise = null;
      const component = type === "grammar" ? grammarReport : feedbackReport;

      const generateVueInstance = () => {
        vueInstancePromise = new Promise((resolve, reject) => {
          try {
            new Vue({
              store: this.$store,
              i18n: this.$i18n,
              extends: component,
              provide: {
                specialForCopyPaste: true,
              },
              propsData: {
                essay: this.essay,
              },
              watch: {
                loading() {
                  if (this.loading === false) {
                    resolve(this);
                  }
                },
              },
            }).$mount();
          } catch (error) {
            reject(error);
          }
        });

        return vueInstancePromise;
      };

      const getRichTextBlob = () => {
        return (
          vueInstancePromise ? vueInstancePromise : generateVueInstance()
        ).then((vm) => {
          let richText = vm.$el.innerHTML;

          if (strIsHTML(richText)) {
            let div = document.createElement("div");
            div.innerHTML = richText;
            richText = div.innerHTML;
            div = null;
          }

          return new Blob([richText], { type: "text/html" });
        });
      };

      const getPlainTextBlob = () => {
        return (
          vueInstancePromise ? vueInstancePromise : generateVueInstance()
        ).then((vm) => {
          return new Blob([vm.$el.innerText], { type: "text/plain" });
        });
      };

      try {
        await copyRichText(getRichTextBlob, getPlainTextBlob);
        // destroy vue instance after copy
        vueInstancePromise && vueInstancePromise.then((vm) => vm?.$destroy());
        vueInstancePromise = null;

        this.$showToastSuccess(
          this.$t("essays.sectionCopiedSuccessfully", { type }),
          { title: this.$t("common.copy"), autoHideDelay: 3000 }
        );
      } catch (error) {
        this.$showToastError(this.$t("essays.errorCopyingSection", { type }));
      }
    },

    async regradeHandler() {
      this.$egModal.show("essay-regrade-modal", {
        component: EssayRegradeModal,
        props: {
          essay: this.essay,
        },
        on: {
          startRegrade: () => this.$emit("regradingChange", true),
          finishRegrade: () => {
            this.$emit("regradingChange", false);
            this.navigateToEssaysList();
          },
        },
      });
    },

    async deleteSingleEssay() {
      try {
        this.loading = true;

        await deleteEssay(this.essay.id);

        this.$router.push("/essays?deleted=true");
      } catch (error) {
        this.loading = false;
        this.error = error.response?.data?.error;
      }
    },

    printEssayFeedback() {
      window.print();
    },

    async navigateToEssaysList() {
      // Navigate to essays list after regrade
      this.$router.replace({
        name: "workspace",
        params: {
          workspaceKey: this.$route.params.workspaceKey,
          id: this.$route.params.id,
        },
      });
    },

    handleStatusChangeSuccess(navigationHint) {
      // Extract status from navigationHint
      const { status } = navigationHint;

      // Handle navigation based on the hint from the button
      if (status === "Graded") {
        return this.$emit("essayStatusChanged", status);
      }

      if (navigationHint.shouldNavigateNext) {
        if (this.canGoNext()) {
          return this.goNext();
        }
        if (this.canGoPrevious()) {
          return this.goPrev();
        }
      }

      this.$emit("essayStatusChanged", status);
    },

    canGoNext() {
      return !this.paginationIsLoading && this.pageInfo?.hasNextPage;
    },

    canGoPrevious() {
      return !this.paginationIsLoading && this.pageInfo?.hasPreviousPage;
    },

    async goNext() {
      if (!this.canGoNext()) {
        return;
      }

      this.paginationIsLoading = true;
      await this.getEssay({ after: this.pageInfo.endCursor });
      await this.$router.push({
        name: "workspace-essay-view",
        params: {
          workspaceKey: this.$route.params.workspaceKey,
          id: this.$route.params.id,
          essayId: this.essay.id,
        },
      });
      this.paginationIsLoading = false;
    },

    async goPrev() {
      if (!this.canGoPrevious()) {
        return;
      }

      this.paginationIsLoading = true;
      await this.getEssay({ before: this.pageInfo.startCursor });
      await this.$router.push({
        name: "workspace-essay-view",
        params: {
          workspaceKey: this.$route.params.workspaceKey,
          id: this.$route.params.id,
          essayId: this.essay.id,
        },
      });
      this.paginationIsLoading = false;
    },

    goToFolder() {
      // NOTE: prevent navigation to folder if pagination to next/previous essay is running
      if (this.paginationIsLoading) return;

      this.$router.push({
        name: "workspace",
        params: {
          workspaceKey: this.$route.params.workspaceKey,
          id: this.$route.params.id,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.essay-header-wrapper {
  border-bottom: 1px solid var(--color-stroke-neutral-default);
}
.essay-header-container {
  flex-direction: row;
  gap: 0;
}

.essay-title {
  min-width: 260px;
  @media (max-width: 1200px) {
    max-width: calc(25vw - 264px);
  }
  @media (min-width: 1201px) and (max-width: 1399px) {
    max-width: calc(35vw - 264px);
  }

  @media (min-width: 1400px) and (max-width: 1699px) {
    max-width: calc(45vw - 264px);
  }

  @media (min-width: 1700px) {
    max-width: calc(55vw - 264px);
  }
}

.header-actions {
  gap: 0.75rem;
}

.divider-vertical {
  width: 1px;
  height: 100%;
  background-color: var(--color-stroke-neutral-default);
}

.pagination {
  .btn {
    padding: 7px 8px;
    line-height: 1;
  }
  .b-icon.bi {
    font-size: 16px;
  }
}

.btn-ai {
  display: inline;

  img {
    display: inline;
    width: 20px;
    height: 20px;

    &.hover {
      display: none;
    }
  }

  &:hover {
    img {
      display: none;

      &.hover {
        display: inline;
      }
    }
  }

  @media (max-width: 991px) {
    span {
      display: none;
    }
  }
}
</style>

<style lang="scss">
.folders-provider.is-sidebar-minimized {
  .essay-view-container .essay-header-container {
    @media (max-width: 999px) {
      flex-direction: column;
      gap: 1rem;
    }
  }
}
@media (max-width: 1240px) {
  .folders-provider:not(.is-sidebar-minimized)
    .essay-view-container
    .essay-header-container {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>
